import { useEffect, useRef } from 'react';
import { CALENDAR_URL, DEMO_URL, track, withUTM } from '@/lib/analytics';

// The lead modal (3 fields → calendar link). Ported from vouch.js `wireModal`.
// Triggers live both in React (the closing section) and in the engine's non-React DOM
// (the scene-7 CTA), so opening is driven by a document-level click delegation on the
// shared [data-*] hooks rather than per-element React handlers — exactly as the original.
export default function BookModal() {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    const modal = dialogRef.current;
    const form = formRef.current;
    if (!modal || !form) return;

    const open = () => {
      track('sw_cta_walkthrough', {});
      if (typeof modal.showModal === 'function') modal.showModal();
      else modal.setAttribute('open', '');
    };
    const close = () => {
      if (typeof modal.close === 'function') modal.close();
      else modal.removeAttribute('open');
    };

    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const t = target?.closest?.('[data-book],[data-close],[data-demo],[data-skip]') as HTMLElement | null;
      if (!t) return;
      if (t.hasAttribute('data-book')) { e.preventDefault(); open(); }
      else if (t.hasAttribute('data-close')) { e.preventDefault(); close(); }
      else if (t.hasAttribute('data-demo')) {
        const url = DEMO_URL;          // same-origin demo — no UTM tracking on the link
        t.setAttribute('href', url);   // keep the anchor correct for modifier-clicks
        track('sw_cta_demo', { where: t.closest('#closing') ? 'closing' : 'chrome' });
        // These anchors render with href="#"; mutating href during the click is not
        // reliably honored by the default navigation, so a plain click would do nothing.
        // Drive the navigation ourselves, but let modifier-clicks open a new tab.
        if (!(e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0)) {
          e.preventDefault();
          window.location.assign(url);
        }
      } else if (t.hasAttribute('data-skip')) {
        track('sw_skip_flight', {});
      }
    };

    const onSubmit = () => {
      const data = new FormData(form);
      track('sw_modal_submit', { company: data.get('company') || '' });
      // hand off to scheduling with UTM passthrough
      window.setTimeout(() => { window.open(withUTM(CALENDAR_URL), '_blank', 'noopener'); }, 60);
    };

    document.addEventListener('click', onClick);
    form.addEventListener('submit', onSubmit);
    return () => {
      document.removeEventListener('click', onClick);
      form.removeEventListener('submit', onSubmit);
    };
  }, []);

  return (
    <dialog id="book-modal" className="v-modal" aria-labelledby="book-title" ref={dialogRef}>
      <form className="v-modal__card" id="book-form" method="dialog" ref={formRef}>
        <button className="v-modal__x" type="button" data-close aria-label="Close">×</button>
        <h3 id="book-title">Book a walkthrough</h3>
        <p>Three fields, then pick a time. Real screens, about fifteen minutes.</p>
        <label>Name<input name="name" required autoComplete="name" /></label>
        <label>Work email<input name="email" type="email" required autoComplete="email" /></label>
        <label>Company<input name="company" required autoComplete="organization" /></label>
        <div className="v-modal__actions">
          <button className="v-btn v-btn--ghost" type="button" data-close>Cancel</button>
          <button className="v-btn v-btn--primary" id="book-submit" type="submit">Request time</button>
        </div>
      </form>
    </dialog>
  );
}
